import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { MAX_FAVORITES } from '@/utility/constants';
import { createFavoriteId } from '@/utility/createFavoriteId';
import { getLocalStorageItem, setLocalStorageItem } from '@/utility/localStorage';
import { Locale } from '@/utility/locale';
const FAVORITE_STORAGE_KEY = 'favorites';
export interface FavoriteItem {
  id: string;
  compositionId: string;
  sellableId: string;
  productTypeId: string;
  appearanceId: string;
  sizeId?: string;
  locale: Locale;
  dateCreated: number;
}
export function useFavorites() {
  const $ = _c(2);
  const locale = useLocale();
  let t0;
  if ($[0] !== locale) {
    t0 = {
      queryKey: ["favorites", locale],
      queryFn: _temp,
      refetchOnReconnect: true,
      refetchOnWindowFocus: "always",
      placeholderData: _temp2
    };
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useQuery(t0);
}
function _temp2(previousData) {
  return previousData;
}
function _temp() {
  return getLocalStorageItem(FAVORITE_STORAGE_KEY) ?? [];
}
export function useAddFavoriteItem() {
  const $ = _c(8);
  const locale = useLocale();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== locale) {
    t0 = async favoriteItem => {
      const favorites = getLocalStorageItem(FAVORITE_STORAGE_KEY) ?? [];
      if (favorites.length >= MAX_FAVORITES) {
        return favorites;
      }
      const id = createFavoriteId(favoriteItem.compositionId, favoriteItem.sellableId, favoriteItem.appearanceId);
      if (favorites.some(existing => existing.id === id)) {
        return favorites;
      }
      const favoriteItemWithId = {
        ...favoriteItem,
        id,
        locale,
        dateCreated: Date.now()
      };
      const updated = [...favorites, favoriteItemWithId];
      setLocalStorageItem(FAVORITE_STORAGE_KEY, updated);
      return updated;
    };
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== locale || $[3] !== queryClient) {
    t1 = () => queryClient.invalidateQueries({
      queryKey: ["favorites", locale]
    });
    $[2] = locale;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: t0,
      onSettled: t1
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useMutation(t2);
}
export function useUpdateFavoriteItem() {
  const $ = _c(3);
  const locale = useLocale();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== locale || $[1] !== queryClient) {
    t0 = {
      mutationFn: _temp3,
      onSettled: () => queryClient.invalidateQueries({
        queryKey: ["favorites", locale]
      })
    };
    $[0] = locale;
    $[1] = queryClient;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useMutation(t0);
}
async function _temp3(favoriteItem) {
  const favorites = getLocalStorageItem(FAVORITE_STORAGE_KEY) ?? [];
  const updated = favorites.reduce((acc, item) => {
    acc.push(item.id === favoriteItem.id ? {
      ...item,
      ...favoriteItem
    } : item);
    return acc;
  }, []);
  setLocalStorageItem(FAVORITE_STORAGE_KEY, updated);
  return updated;
}
export function useRemoveFavoriteItems() {
  const $ = _c(3);
  const locale = useLocale();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== locale || $[1] !== queryClient) {
    t0 = {
      mutationFn: _temp4,
      onSettled: () => queryClient.invalidateQueries({
        queryKey: ["favorites", locale]
      })
    };
    $[0] = locale;
    $[1] = queryClient;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useMutation(t0);
}
async function _temp4(favoriteItemIds) {
  const favorites = getLocalStorageItem(FAVORITE_STORAGE_KEY);
  if (!favorites) {
    return null;
  }
  const idsToRemove = new Set(favoriteItemIds);
  const updated = favorites.filter(item => !idsToRemove.has(item.id));
  if (updated.length === favorites.length) {
    return null;
  }
  setLocalStorageItem(FAVORITE_STORAGE_KEY, updated);
  return updated;
}
export function useRemoveFavoriteItem() {
  const $ = _c(3);
  const locale = useLocale();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== locale || $[1] !== queryClient) {
    t0 = {
      mutationFn: _temp5,
      onSettled: () => queryClient.invalidateQueries({
        queryKey: ["favorites", locale]
      })
    };
    $[0] = locale;
    $[1] = queryClient;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useMutation(t0);
}
async function _temp5(favoriteItemId) {
  const favorites = getLocalStorageItem(FAVORITE_STORAGE_KEY);
  if (!favorites) {
    return null;
  }
  const updated = favorites.reduce((acc, item) => {
    if (item.id !== favoriteItemId) {
      acc.push(item);
    }
    return acc;
  }, []);
  if (updated.length === favorites.length) {
    return null;
  }
  setLocalStorageItem(FAVORITE_STORAGE_KEY, updated);
  return updated;
}